import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { EventModel } from "../../api/events/EventModel";
import { EventAdapter } from "../../api/events/EventAdapter";
import type {
  Event,
  EventParticipant,
  CreateEventRequest,
  UpdateEventRequest,
  RegisterParticipantRequest,
} from "../../api/events/Event.typed";

interface EventContextType {
  getEvents: () => Promise<Event[] | undefined>;
  getEvent: (id: number) => Promise<Event | undefined>;
  createEvent: (event: CreateEventRequest) => Promise<Event | undefined>;
  updateEvent: (event: UpdateEventRequest) => Promise<Event | undefined>;
  updateEventSilent: (event: UpdateEventRequest) => Promise<Event | undefined>;
  deleteEvent: (id: number) => Promise<boolean | undefined>;
  getEventParticipants: (eventId: number) => Promise<EventParticipant[] | undefined>;
  registerForEvent: (participant: RegisterParticipantRequest) => Promise<void>;
  deleteEventParticipant: (eventId: number, participantId: number) => Promise<void>;
  toggleEventOnline: (eventId: number, online: boolean) => Promise<boolean | undefined>;
  uploadEventBackground: (eventId: number, file: File) => Promise<string | undefined>;
}

const EventContext = React.createContext<EventContextType>(null!);

export function EventProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new EventModel(appConfig);

    return new EventAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");

        return undefined;
      }
    },
    [showAlert],
  );

  const getEvents = React.useCallback(
    async (): Promise<Event[] | undefined> =>
      adapter ? wrap(() => adapter.getEvents()) : undefined,
    [adapter, wrap],
  );

  const getEvent = React.useCallback(
    async (id: number): Promise<Event | undefined> =>
      adapter ? wrap(() => adapter.getEvent(id)) : undefined,
    [adapter, wrap],
  );

  const createEvent = React.useCallback(
    async (event: CreateEventRequest): Promise<Event | undefined> => {
      if (!adapter) return undefined;
      const created = await wrap(() => adapter.createEvent(event));
      if (created) showAlert("Événement créé", "success");

      return created;
    },
    [adapter, wrap, showAlert],
  );

  const updateEvent = React.useCallback(
    async (event: UpdateEventRequest): Promise<Event | undefined> => {
      if (!adapter) return undefined;
      const updated = await wrap(() => adapter.updateEvent(event));
      if (updated) showAlert("Événement mis à jour", "success");

      return updated;
    },
    [adapter, wrap, showAlert],
  );

  const updateEventSilent = React.useCallback(
    async (event: UpdateEventRequest): Promise<Event | undefined> => {
      if (!adapter) return undefined;

      return wrap(() => adapter.updateEvent(event));
    },
    [adapter, wrap],
  );

  const deleteEvent = React.useCallback(
    async (id: number): Promise<boolean | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.deleteEvent(id);

        return true;
      });
      if (result) showAlert("Événement supprimé", "success");

      return result;
    },
    [adapter, wrap, showAlert],
  );

  const getEventParticipants = React.useCallback(
    async (eventId: number): Promise<EventParticipant[] | undefined> =>
      adapter ? wrap(() => adapter.getEventParticipants(eventId)) : undefined,
    [adapter, wrap],
  );

  const registerForEvent = React.useCallback(
    async (participant: RegisterParticipantRequest): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.registerForEvent(participant);
        showAlert("Inscription enregistrée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteEventParticipant = React.useCallback(
    async (eventId: number, participantId: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteEventParticipant(eventId, participantId);
        showAlert("Participant supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const toggleEventOnline = React.useCallback(
    async (eventId: number, online: boolean): Promise<boolean | undefined> => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.toggleEventOnline(eventId, online);

        return true;
      });
      if (result) {
        showAlert(online ? "Événement mis en ligne" : "Événement mis hors-ligne", "success");
      }

      return result;
    },
    [adapter, wrap, showAlert],
  );

  const uploadEventBackground = React.useCallback(
    async (eventId: number, file: File): Promise<string | undefined> => {
      if (!adapter) return undefined;
      const url = await wrap(() => adapter.uploadEventBackground(eventId, file));
      if (url) showAlert("Image de fond mise à jour", "success");

      return url;
    },
    [adapter, wrap, showAlert],
  );

  const value = React.useMemo<EventContextType>(
    () => ({
      getEvents,
      getEvent,
      createEvent,
      updateEvent,
      updateEventSilent,
      deleteEvent,
      getEventParticipants,
      registerForEvent,
      deleteEventParticipant,
      toggleEventOnline,
      uploadEventBackground,
    }),
    [
      getEvents,
      getEvent,
      createEvent,
      updateEvent,
      updateEventSilent,
      deleteEvent,
      getEventParticipants,
      registerForEvent,
      deleteEventParticipant,
      toggleEventOnline,
      uploadEventBackground,
    ],
  );

  return <EventContext.Provider value={value}>{props.children}</EventContext.Provider>;
}

export function useEvent(): EventContextType {
  const ctx = React.useContext(EventContext);
  if (!ctx) throw new Error("useEvent must be used within EventProvider");

  return ctx;
}
